import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/endpoints.js';
import { clearSession, restoreSession, setUnauthorizedHandler } from '../api/client.js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  // A signed-in user is restored from the stored refresh token on load so a
  // refresh of the page does not bounce them back to the sign-in screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await restoreSession();
        if (cancelled) return;
        if (data?.user) setUser(data.user);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The client calls this when a refresh no longer works, so the whole app can
  // drop back to signed-out rather than each screen handling it separately.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async (email, password) => {
    const data = await authApi.login({ email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await authApi.signup(payload);
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const verifyEmail = useCallback(async (payload) => {
    const data = await authApi.verifyEmail(payload);
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const signOut = useCallback(async () => {
    // Revoke the session on the server, but never let a failed sign-out keep
    // the user stuck in the app.
    try {
      await authApi.logout();
    } catch {
      /* the local session is cleared regardless */
    }
    clearSession();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const data = await authApi.me();
    setUser(data.user);
    return data.user;
  }, []);

  /** Save name and phone, then keep the session's copy in step with the server. */
  const updateProfile = useCallback(async (patch) => {
    const data = await authApi.updateMe(patch);
    setUser(data.user);
    return data.user;
  }, []);

  const value = useMemo(
    () => ({
      user,
      ready,
      isVerified: Boolean(user?.emailVerified),
      isSchoolStaff: user?.role === 'school_staff',
      isPlatformAdmin: user?.role === 'platform_admin',
      signIn,
      signOut,
      register,
      verifyEmail,
      refreshUser,
      updateProfile
    }),
    [user, ready, signIn, signOut, register, verifyEmail, refreshUser, updateProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
